import { type ForwardedRef, forwardRef, type ReactNode, useCallback } from 'react';

import TreeSelect, {
  type LabelT,
  type TreeSelectInterface,
} from '@/components/Select/TreeSelect';
import treeSelectUtils from '@/components/Select/treeSelectUtils';
import type { TreeSelectItemBase } from '@/components/Select/types';
import { CHART_SOURCE_SEPERATOR } from '@/entities/chart/model/constants';
import useChartFieldFetcher from '@/features/(charts)/chart-controller/hooks/use-chart-field-fetcher';
import AllSelectFooter, {
  type TOPIC_LEVEL_FILTER,
} from '@/features/(charts)/chart-list/ui/all-select-footer';
import { useToast } from '@/stores/pure/ToastStore';
import { SOURCE_COLUMNS } from '@/types/filterKeyConst';

type Props = {
  initialOpen?: boolean;
  initialized: boolean;
  style?: React.CSSProperties;
  placeholder?: string;
  filterOption: FilterOptionT | null;
  values: StringNumberValue[];
  metaValues?: SegmentMeta[];
  children?: ReactNode;
  loading?: boolean;
  onClear: () => void;
  onChange: (values: StringNumberValue[]) => void;
  header?: (params: TreeSelectInterface<{}>) => ReactNode;
  showTopAction?: boolean;
};

const ChartFieldSelect = (props: Props, ref: ForwardedRef<HTMLDivElement>) => {
  const addToast = useToast();
  const customFilterItem = props.filterOption?.ChartFilterItem;

  const fetcher = useChartFieldFetcher({
    filterOption: props.filterOption,
  });

  const parser = useCallback(
    (dataList: TreeSelectItemBase[], values: StringNumberValue[]) => {
      let result: { key: StringNumberValue; node: ReactNode }[] = [];
      if (
        treeSelectUtils.isDisallowAllSelectColumn(props.filterOption?.columnKey)
      ) {
        result = values.map((value) => {
          const target = treeSelectUtils.findNode(dataList, value);
          if (target) {
            return {
              key: target.alias,
              node: target.node,
            };
          }
          if (props.metaValues) {
            const metaTarget = props.metaValues.find(
              (item) => item.alias === value,
            );
            if (metaTarget) {
              return {
                key: metaTarget?.alias || '',
                node: metaTarget?.name || '',
              };
            }
          }
          return {
            key: value,
            node: value,
          };
        });
      } else {
        values.forEach((value) => {
          const target = treeSelectUtils.findNode(dataList, value);
          const metaTarget = props.metaValues?.find(
            (item) => item.alias === value,
          );
          if (target) {
            result.push({
              key: target.alias,
              node: target.node,
            });
            return;
          }
          if (metaTarget) {
            result.push({
              key: metaTarget.alias,
              node: metaTarget.name,
            });
            return;
          }
          result.push({
            key: value,
            node: value,
          });
        });
      }
      return result;
    },
    [props.filterOption?.columnKey, props.metaValues],
  );

  const chartFilterValidateFunction = useCallback(
    (values: StringNumberValue[]) => {
      if (props.filterOption?.columnKey === SOURCE_COLUMNS.SOURCE_TYPE) {
        const splited = values[0]?.toString().split(CHART_SOURCE_SEPERATOR);
        if (!splited) return true;
        const parent = splited[splited.length - 2];
        const isValidate = values.every((value) => {
          const splitSegment = value.toString().split(CHART_SOURCE_SEPERATOR);
          const splitedParent = splitSegment[splitSegment.length - 2];

          return splitedParent === parent;
        });
        if (!isValidate) {
          addToast(
            'You can only select at the same level and within the same source.',
            'fail',
          );
        }
        return isValidate;
      }
      return true;
    },
    [addToast, props.filterOption?.columnKey],
  );

  if (customFilterItem) {
    return customFilterItem({
      values: props.values,
      onChange: props.onChange,
      disabled: props.loading,
      showTopAction: props.showTopAction,
    } as ChartFilterItemProps & {
      showTopAction?: boolean;
    });
  }

  const onLevelSelect = (
    value: TOPIC_LEVEL_FILTER,
    dataList: TreeSelectItemBase<{ level?: number }>[],
    onValueChange: (labels: LabelT[]) => void,
  ) => {
    let children = treeSelectUtils
      .getFlattenChildNodes(dataList)
      .filter((data) => data.meta?.level === value);
    let newValues = children
      .map((data) => ({
        key: data.alias,
        node: data.node,
      }))
      .filter((data) => !!props.values.find((item) => item === data.key));
    onValueChange(newValues);
  };

  return (
    props.initialized && (
      <TreeSelect
        {...props}
        searchable={props.filterOption?.searchable}
        parser={parser}
        fetcher={fetcher}
        ref={ref}
        header={props.header}
        validate={chartFilterValidateFunction}
        loading={props.loading}
        footer={({ onSelectAll, onDeselectAll, dataList, onValueChange }) =>
          !treeSelectUtils.isDisallowAllSelectColumn(
            props.filterOption?.columnKey,
          ) && (
            <AllSelectFooter
              onSelectAll={onSelectAll}
              onDeselectAll={onDeselectAll}
              onLevelSelect={(level) =>
                onLevelSelect(level, dataList, onValueChange)
              }
              filterOption={props.filterOption}
            />
          )
        }
      />
    )
  );
};

export default forwardRef(ChartFieldSelect);
